import React, { useState, useMemo } from "react";
import { every, groupBy } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Button,
    Checkbox,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText
} from "@mui/material/";
import actions from "../../../../Store/actions";
import { strings } from "../../../../common/i18n";
import { enums, helpers } from "../../../../common/lib";
import styles from "./styles";
import { logToSentry } from "../../../../config/Sentry";
import { Typography } from "@mui/material/";
import { useReissuedAsset } from "../../../../common/hooks";

const canAddAssignment = (auditAssignments: any) =>
    every(
        auditAssignments,
        (item) =>
            item == null ||
            item.status === enums.status.auditAssignment.recalled ||
            item.status === enums.status.auditAssignment.submitted
    );

type Props = {
    onClose: Function,
    onSaveComplete: Function,
    open: boolean
};

/**
 * Edit Audit Assignment Add Dialog Component
 */
export default ({ onClose, onSaveComplete }: Props) => {
    const {
        auth,
        accounts,
        auditAssets,
        auditAssignments,
        auditLocations,
        locations,
        userLocations,
        auditsList,
        auditReview
    } = useSelector((state) => state);
    const name = auth.account.displayName;
    const { reissued } = auditReview.auditAssets;
    const [assignments, setAssignments] = useState({});
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [selectedAuditors, setSelectedAuditors] = useState([]);
    const auditId = auditsList.item.id;

    const canReissueAssets = useReissuedAsset();

    const onAuditorSelection = (value) => () => {
        const { auditLocationId } = data[0];
        const currentIndex = selectedAuditors.indexOf(value);
        const newselectedAuditors = [...selectedAuditors];

        if (currentIndex === -1) {
            newselectedAuditors.push(value);
        } else {
            newselectedAuditors.splice(currentIndex, 1);
        }
        const next = { ...assignments };
        next[auditLocationId] = newselectedAuditors;
        setAssignments(next);

        setSelectedAuditors(newselectedAuditors);
    };

    const data = useMemo(() => {
        const byAuditLocation = groupBy(auditAssets.data, "auditLocation");
        return auditLocations.data.map((item) => {
            const auditors = [];
            userLocations.data.forEach(({ group, location, user }) => {
                if (
                    group === enums.auth.group.auditor &&
                    location === item.location
                ) {
                    const forAuditorAuditLocation =
                        auditAssignments.data.filter(
                            (i) =>
                                i.auditor === user &&
                                i.auditLocation === item.id
                        );
                    const auditor = helpers.getListItemForId(accounts, user);
                    if (
                        auditor &&
                        Object.keys(auditor).length &&
                        canAddAssignment(forAuditorAuditLocation)
                    ) {
                        const { email, firstName, id, surname } = auditor;
                        auditors.push({
                            id,
                            name: `${email} \u2014 ${firstName} ${surname}`
                        });
                    }
                }
            });
            const location = helpers.getListItemForId(locations, item.location);
            const forLocation = byAuditLocation[item.id] || [];
            return {
                assigned: forLocation.filter((item) => item.isAssigned).length,
                auditLocationId: item.id,
                auditors,
                location: location.name,
                mandatory: forLocation.filter((item) => item.isMandatory)
                    .length,
                reissued: forLocation.filter(
                    (item) =>
                        item.reissuedStatus === enums.status.reissued.reissued
                ).length
            };
        });
    }, [
        accounts,
        auditAssets,
        auditAssignments,
        auditLocations,
        locations,
        userLocations
    ]);

    const onDialogClose = () => !loading && onClose && onClose();

    const onSave = async () => {
        setLoading(true);

        const next = Object.entries(assignments).reduce(
            (aggregate, [key, value]) => [
                ...aggregate,
                ...value.map((auditor) => ({
                    auditLocation: Number(key),
                    is_reissued: canReissueAssets,
                    auditor,
                    createdBy: auth.account.id,
                    status: 1
                }))
            ],
            []
        );

        if (next.length)
            try {
                await dispatch(
                    actions.auditAssignments.create.postRequest(next)
                );
                await dispatch(
                    actions.assignmentDetails.create.getRequest(auditId)
                ),
                    dispatch(
                        actions.toast.create.success(
                            strings("messages.assetReissudToAuditors")
                        )
                    );
            } catch (error) {
                const errStr = error.toString();
                console.error(errStr);
                logToSentry("On reissuing assets:", { error });
                dispatch(
                    actions.toast.create.error(
                        strings("errors.assetReissudToAuditors")
                    )
                );
            }
        setLoading(false);
        onClose();
        onSaveComplete && onSaveComplete();
    };

    return (
        <>
            <Box sx={styles.auditorListContainer}>
                {canReissueAssets && (
                    <>
                        <Typography variant="body2">
                            {strings("messages.reissueAssets1", { name })}
                        </Typography>
                        <Typography
                            variant="body2"
                            style={{ fontWeight: "bold" }}
                        >
                            {strings("messages.reissueAssets2", { reissued })}
                        </Typography>
                        <Typography variant="body2">
                            {strings("messages.reissueAssets3", { reissued })}
                        </Typography>
                    </>
                )}
                <List sx={styles.auditorListContainer}>
                    {data &&
                        data.length > 0 &&
                        data[0].auditors &&
                        data[0].auditors.map((item) => {
                            const { id, name } = item;
                            const labelId = `checkbox-list-label-${id}`;

                            return (
                                <ListItem key={id} disablePadding>
                                    <ListItemButton
                                        onClick={onAuditorSelection(id)}
                                        dense
                                    >
                                        <ListItemIcon>
                                            <Checkbox
                                                disabled={
                                                    data[0].assigned ===
                                                        data[0].mandatory &&
                                                    data[0].reissued === 0
                                                }
                                                edge={strings("start")}
                                                checked={selectedAuditors.includes(
                                                    id
                                                )}
                                                tabIndex={-1}
                                                disableRipple
                                                inputProps={{
                                                    "aria-labelledby": labelId
                                                }}
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            id={labelId}
                                            primary={name}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                </List>
            </Box>
            <Box sx={styles.auditorActionContainer}>
                <Button
                    sx={styles.secondaryButton}
                    disabled={loading}
                    onClick={onDialogClose}
                >
                    {strings("cancel")}
                </Button>
                <Button
                    variant="contained"
                    sx={styles.primaryButton}
                    disabled={selectedAuditors.length < 1 || loading}
                    onClick={onSave}
                >
                    {strings("save")}
                </Button>
            </Box>
        </>
    );
};
